import { forwardRef } from 'react';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';
import { StatCount } from 'components/common/stats';

import type { IconProp } from 'components/common/icons/Icon';
import type { StatCategory } from 'components/common/stats/StatCount';

export type DropdownItemProps = {
  checked?: 'indeterminate' | boolean;
  count?: number;
  countCategory?: StatCategory;
  forceActive?: boolean;
  icon?: IconProp;
  label: string;
  labelSubtitle?: string | React.ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  onClick?: (event) => void;
  showArrow?: boolean;
  withCheckbox?: boolean;
  withRadioCheck?: boolean;
};

const DropdownItem = forwardRef(
  (
    props: DropdownItemProps,
    forwardedRef: React.ForwardedRef<HTMLDivElement>
  ) => {
    const { theme } = useReduxState();

    const {
      checked,
      count,
      countCategory = 'success',
      forceActive,
      icon,
      label,
      labelSubtitle,
      onClick,
      showArrow,
      withCheckbox,
      withRadioCheck,
      ...otherProps
    } = props;

    return (
      <div
        className={`item ${forceActive ? 'item-active' : ''}`}
        ref={forwardedRef}
        onClick={onClick}
        {...otherProps}
      >
        <div className="item-inner">
          {icon ? (
            <div className="icon">
              <Icon icon={icon} fixedWidth={true} />
            </div>
          ) : null}

          {withCheckbox ? (
            <div
              className={`checkbox ${
                checked === 'indeterminate'
                  ? 'indeterminate'
                  : checked
                  ? 'checked'
                  : ''
              }`}
            >
              <Icon icon={checked === 'indeterminate' ? 'minus' : 'check'} />
            </div>
          ) : null}

          {withRadioCheck ? (
            <div className="radio-check">
              <Icon icon="check" />
            </div>
          ) : null}

          <div className="label">
            {label}

            {labelSubtitle ? (
              <div className="subtitle">{labelSubtitle}</div>
            ) : null}
          </div>

          <div className="count">
            {count > 0 ? (
              <StatCount
                category={countCategory}
                style={{ margin: '-1px 0 -1px -4px' }}
                value={count}
              />
            ) : null}
          </div>

          {showArrow ? (
            <div className="arrow">
              <Icon icon="chevron-right" />
            </div>
          ) : null}
        </div>

        <style jsx>{`
          .item {
            color: ${theme.textColorLight};
            cursor: pointer;
            font-size: 1.5rem;
            font-weight: 600;
            outline: 0;
            padding: 5px 5px;
          }
          .item:not(:first-child) {
            border-top: 1px solid ${theme.lightGrey};
          }
          .item-inner {
            align-items: flex-start;
            border-radius: ${theme.dropdownBorderRadius};
            display: flex;
            gap: 10px;
            justify-content: space-between;
            padding: 8px 10px;
          }
          .item.item-active .item-inner,
          .item:hover .item-inner,
          .item[data-highlighted] .item-inner {
            background-color: ${theme.lightGreyLighter};
          }
          .icon {
            font-size: 1.3rem;
            padding-top: 2px;
          }
          .checkbox {
            background-color: rgba(0, 0, 0, 0.1);
            border: 0;
            border-radius: 5px;
            box-shadow: none;
            color: transparent;
            display: inline;
            flex-shrink: 0;
            font-size: 0.8rem;
            font-weight: 400;
            padding-top: 4px;
            position: relative;
            text-align: center;
            top: 1px;
            transition: background-color 0.1s, color 0.1s;
            height: 18px;
            width: 18px;
          }
          .checkbox.checked,
          .checkbox.indeterminate {
            background-color: ${theme.successColor};
            color: ${theme.successTextColor};
          }
          .radio-check {
            background-color: rgba(0, 0, 0, 0.1);
            border: 0;
            border-radius: 18px;
            box-shadow: none;
            color: transparent;
            display: inline;
            flex-shrink: 0;
            font-size: 0.8rem;
            font-weight: 400;
            padding-top: 4px;
            position: relative;
            text-align: center;
            top: 1px;
            transition: background-color 0.1s, color 0.1s;
            height: 18px;
            width: 18px;
          }
          .item[data-state='checked'] .radio-check {
            background-color: ${theme.successColor};
            color: ${theme.successTextColor};
          }
          .label {
            flex-grow: 1;
          }
          .subtitle {
            color: ${theme.textColorLighter};
            font-size: 1.2rem;
            margin-top: 2px;
          }
          .arrow {
            color: ${theme.textColorLight};
            font-size: 1.1rem;
            opacity: 0.3;
            padding-top: 4px;
          }
        `}</style>
      </div>
    );
  }
);

export default DropdownItem;
